/**
 * Copyright © 2025-2026 Indisea Software LLC. All rights reserved.
 *
 * Proprietary and confidential. Licensed, not sold. Use is subject to the
 * license agreement between Indisea Software LLC and the licensee.
 * See LICENSE for full terms.
 *
 * SPDX-License-Identifier: LicenseRef-Indisea-Proprietary
 */

import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle,
  DialogContent, 
  DialogActions,
  Button, 
  Typography,
  Box
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export default function SessionExpiredDialog() {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [isRedirecting, setIsRedirecting] = useState(false);

  useEffect(() => {
    const handleSessionExpired = (event) => {
      setReason(event.detail?.reason || 'unknown');
      setOpen(true);
    };

    window.addEventListener('sessionExpired', handleSessionExpired);

    return () => {
      window.removeEventListener('sessionExpired', handleSessionExpired);
    };
  }, []);

  // Re-authenticate via the app's own OAuth connect flow (same one used by
  // ConnectPage/AuthProvider) so QBO redirects back into this app afterward,
  // instead of Intuit's generic sign-in page which has nowhere app-specific
  // to send the user back to.
  const handleRedirect = async () => {
    setIsRedirecting(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_CONNECTOR_API_URL}/api/auth/connect`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        const data = await response.json();
        if (data?.url) {
          window.location.href = data.url;
          return;
        }
      }
    } catch (err) {
      console.error('Failed to initiate re-authentication', err);
    }

    // Fallback: send the user back into the app if OAuth initiation failed
    window.location.href = '/launch';
  };

  const isIdleTimeout = reason === 'idle-timeout' || reason === 'inactivity';
  
  return (
    <Dialog 
      open={open} 
      onClose={() => {}} 
      disableEscapeKeyDown 
      maxWidth="sm"
      PaperProps={{
        sx: { minWidth: 420 }
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ErrorOutlineIcon sx={{ color: 'warning.main', fontSize: 28 }} />
          <Typography 
            variant="h6" 
            component="span"
            sx={{ 
              fontWeight: 600,
              fontFamily: '"Segoe UI", "Roboto", sans-serif'
            }}
          >
Session Expired
          </Typography>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        <Typography 
          sx={{ 
            color: 'text.secondary',
            whiteSpace: 'nowrap',
            fontFamily: '"Segoe UI", "Roboto", sans-serif',
            fontSize: '0.95rem',
            fontWeight: 500
          }}
        >
          {isIdleTimeout 
            ? 'Your session has expired due to inactivity. Please sign in again.'
            : 'Your session has expired. Please sign in again.'
          }
        </Typography>
      </DialogContent>
      
      <DialogActions sx={{ justifyContent: 'center', px: 3, pb: 3 }}>
        <Button
          variant="contained"
          onClick={handleRedirect}
          disabled={isRedirecting}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            px: 4
          }}
        >
          {isRedirecting ? 'Redirecting…' : 'Sign In Again'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
